(() => {
  'use strict';

  const LOAD_PROMISE_KEY = '__pevGoogleMapsLoadPromise';
  const CALLBACK_NAME = '__pevGoogleMapsReady';
  const LOAD_TIMEOUT_MS = 18000;

  const isGoogleReady = () => Boolean(window.google?.maps?.importLibrary);

  const waitForExistingGoogleScript = (script) => new Promise((resolve, reject) => {
    if (isGoogleReady()) {
      resolve(window.google);
      return;
    }

    let finished = false;
    const startedAt = Date.now();

    const finish = (callback, value) => {
      if (finished) return;
      finished = true;
      window.clearInterval(pollTimer);
      window.clearTimeout(timeoutTimer);
      script?.removeEventListener('error', onError);
      callback(value);
    };

    const onError = () => finish(reject, new Error('Google Maps could not load.'));
    const pollTimer = window.setInterval(() => {
      if (isGoogleReady()) {
        finish(resolve, window.google);
      } else if (Date.now() - startedAt > LOAD_TIMEOUT_MS) {
        finish(reject, new Error('Google Maps timed out while loading.'));
      }
    }, 100);
    const timeoutTimer = window.setTimeout(() => {
      finish(reject, new Error('Google Maps timed out while loading.'));
    }, LOAD_TIMEOUT_MS + 250);

    script?.addEventListener('error', onError, { once: true });
  });

  const loadGoogleMaps = (apiKey) => {
    if (isGoogleReady()) return Promise.resolve(window.google);
    if (window[LOAD_PROMISE_KEY]) return window[LOAD_PROMISE_KEY];

    const loadPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[data-pev-google-maps], script[src*="maps.googleapis.com/maps/api/js"]'
      );

      if (existingScript) {
        waitForExistingGoogleScript(existingScript).then(resolve).catch(reject);
        return;
      }

      const script = document.createElement('script');
      const language = (document.documentElement.lang || 'en-GB').trim() || 'en-GB';
      const params = new URLSearchParams({
        key: apiKey,
        v: 'weekly',
        loading: 'async',
        libraries: 'places',
        callback: CALLBACK_NAME,
        language,
        region: 'GB',
        auth_referrer_policy: 'origin',
      });

      const cleanUp = () => {
        window.clearTimeout(timeout);
        delete window[CALLBACK_NAME];
      };

      const fail = (error) => {
        cleanUp();
        script.remove();
        reject(error);
      };

      const timeout = window.setTimeout(() => {
        fail(new Error('Google Maps timed out while loading.'));
      }, LOAD_TIMEOUT_MS);

      window[CALLBACK_NAME] = () => {
        cleanUp();
        if (isGoogleReady()) {
          resolve(window.google);
        } else {
          fail(new Error('The Google Maps Places library is unavailable.'));
        }
      };

      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.dataset.pevGoogleMaps = 'true';
      script.onerror = () => fail(new Error('Google Maps could not load.'));
      document.head.appendChild(script);
    });

    window[LOAD_PROMISE_KEY] = loadPromise.catch((error) => {
      delete window[LOAD_PROMISE_KEY];
      throw error;
    });

    return window[LOAD_PROMISE_KEY];
  };

  const createElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  };

  const safeHttpsUrl = (value) => {
    if (!value) return '';
    try {
      const url = new URL(String(value), window.location.origin);
      return url.protocol === 'https:' ? url.href : '';
    } catch (_error) {
      return '';
    }
  };

  const setExternalLink = (link, url) => {
    const safeUrl = safeHttpsUrl(url);
    if (!link || !safeUrl) return false;
    link.href = safeUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    return true;
  };

  const createStars = (rating) => {
    const numericRating = Number(rating) || 0;
    const roundedRating = Math.max(0, Math.min(5, Math.round(numericRating)));
    const stars = createElement('div', 'pev-google-review__stars');
    stars.setAttribute('aria-label', `${numericRating.toFixed(1)} out of 5 stars`);

    for (let index = 1; index <= 5; index += 1) {
      const star = createElement('span', index > roundedRating ? 'is-muted' : '', '★');
      star.setAttribute('aria-hidden', 'true');
      stars.appendChild(star);
    }

    return stars;
  };

  const createReviewer = (review) => {
    const attribution = review.authorAttribution || {};
    const wrapper = createElement('div', 'pev-google-review__author');
    const photoUrl = safeHttpsUrl(attribution.photoURI);

    if (photoUrl) {
      const image = document.createElement('img');
      image.className = 'pev-google-review__avatar';
      image.src = photoUrl;
      image.alt = '';
      image.width = 44;
      image.height = 44;
      image.loading = 'lazy';
      image.referrerPolicy = 'no-referrer';
      wrapper.appendChild(image);
    } else {
      const initial = (attribution.displayName || 'G').trim().charAt(0).toUpperCase() || 'G';
      const avatar = createElement('span', 'pev-google-review__avatar pev-google-review__avatar--initial', initial);
      avatar.setAttribute('aria-hidden', 'true');
      wrapper.appendChild(avatar);
    }

    const identity = createElement('div', 'pev-google-review__identity');
    const authorName = attribution.displayName || 'Google Maps reviewer';
    const authorLink = createElement('a', '', authorName);

    if (setExternalLink(authorLink, attribution.uri)) {
      identity.appendChild(authorLink);
    } else {
      identity.appendChild(createElement('strong', '', authorName));
    }

    identity.appendChild(createElement(
      'span',
      '',
      review.relativePublishTimeDescription || 'Google Maps review'
    ));

    wrapper.appendChild(identity);
    return wrapper;
  };

  const createVisitDate = (review) => {
    const year = Number(review.visitDateYear);
    const month = Number(review.visitDateMonth);
    if (!Number.isInteger(year) || !Number.isInteger(month) || month < 0 || month > 11) return null;

    const formatted = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month, 1)));

    return createElement('p', 'pev-google-review__visit-date', `Visited ${formatted}`);
  };

  const createReviewCard = (review) => {
    const card = createElement('article', 'pev-google-review pev-google-review--live');
    card.appendChild(createReviewer(review));
    card.appendChild(createStars(review.rating));

    const reviewText = review.text || review.originalText || 'Rating-only review.';
    card.appendChild(createElement('blockquote', '', reviewText));

    const visitDate = createVisitDate(review);
    if (visitDate) card.appendChild(visitDate);

    const originalText = String(review.originalText || '').trim();
    const localizedText = String(review.text || '').trim();
    if (originalText && localizedText && originalText !== localizedText) {
      card.appendChild(createElement(
        'p',
        'pev-google-review__translation',
        'Translated by Google Maps. Open the source review to see the original text.'
      ));
    }

    const actions = createElement('footer', 'pev-google-review__actions');
    const sourceLink = createElement('a', 'pev-google-review__source-link', 'View on Google Maps');
    if (setExternalLink(sourceLink, review.googleMapsURI)) actions.appendChild(sourceLink);

    const reportLink = createElement('a', 'pev-google-review__report-link', 'Report review');
    if (setExternalLink(reportLink, review.flagContentURI)) actions.appendChild(reportLink);

    if (!actions.childElementCount) {
      actions.appendChild(createElement('span', '', 'Google Maps review'));
    }

    card.appendChild(actions);
    return card;
  };

  const updateLinks = (section, place) => {
    const links = place.googleMapsLinks || {};
    const profileUrl = safeHttpsUrl(
      links.reviewsURI || links.placeURI || place.googleMapsURI || section.dataset.profileUrl
    );
    const writeUrl = safeHttpsUrl(links.writeAReviewURI);
    const readButton = section.querySelector('[data-pev-google-read-button]');
    const writeButton = section.querySelector('[data-pev-google-write-button]');
    const fallbackCard = section.querySelector('[data-pev-google-fallback-card]');

    if (profileUrl) {
      if (readButton) readButton.href = profileUrl;
      if (fallbackCard) fallbackCard.href = profileUrl;
    }

    if (writeButton && writeUrl) {
      writeButton.href = writeUrl;
      writeButton.hidden = false;
    }
  };

  const updateRating = (section, place) => {
    const ratingTarget = section.querySelector('[data-pev-google-rating-text]');
    if (!ratingTarget || !Number.isFinite(Number(place.rating))) return;

    const rating = Number(place.rating).toFixed(1);
    const count = Number(place.userRatingCount || 0);
    const placeName = String(place.displayName || '').trim();
    const ratingLabel = count > 0
      ? `${rating} from ${count.toLocaleString('en-GB')} Google Maps ratings`
      : `${rating} on Google Maps`;

    ratingTarget.textContent = placeName ? `${placeName}: ${ratingLabel}` : ratingLabel;
  };

  const updateThirdPartyAttributions = (section, place) => {
    const target = section.querySelector('[data-pev-google-third-party]');
    if (!target) return;

    const attributions = Array.isArray(place.attributions) ? place.attributions : [];
    target.replaceChildren();

    let renderedAttributions = 0;
    attributions.forEach((attribution) => {
      if (!attribution?.provider) return;
      if (renderedAttributions > 0) target.appendChild(document.createTextNode(', '));
      const link = createElement('a', '', attribution.provider);
      if (setExternalLink(link, attribution.providerURI)) {
        target.appendChild(link);
      } else {
        target.appendChild(document.createTextNode(attribution.provider));
      }
      renderedAttributions += 1;
    });

    target.hidden = target.childNodes.length === 0;
  };

  const setSearchDisclosure = (section, usedTextSearch) => {
    const note = section.querySelector('[data-pev-google-search-note]');
    if (!note) return;
    note.hidden = !usedTextSearch;
  };

  const showFailure = (section, message) => {
    const liveContainer = section.querySelector('[data-pev-google-live]');
    const loading = section.querySelector('[data-pev-google-loading]');
    const grid = section.querySelector('[data-pev-google-grid]');
    const error = section.querySelector('[data-pev-google-error]');
    const disclosure = section.querySelector('[data-pev-google-disclosure]');
    const fallback = section.querySelector('[data-pev-google-fallback-content]');

    if (liveContainer) liveContainer.setAttribute('aria-busy', 'false');
    if (loading) loading.hidden = true;
    if (grid) grid.hidden = true;
    if (disclosure) disclosure.hidden = true;
    if (fallback) fallback.hidden = false;

    if (error) {
      error.textContent = window.Shopify?.designMode ? message : '';
      error.hidden = !window.Shopify?.designMode;
    }
  };

  const getPlace = async (Place, placeId, placeQuery) => {
    if (placeId) {
      return { place: new Place({ id: placeId }), usedTextSearch: false };
    }

    const { places } = await Place.searchByText({
      textQuery: placeQuery,
      fields: ['id', 'displayName'],
      language: 'en-GB',
      region: 'gb',
      maxResultCount: 1,
    });

    if (!places || places.length === 0) {
      throw new Error('No matching Google Business Profile was found. Add the exact Place ID in Theme settings.');
    }

    return { place: places[0], usedTextSearch: true };
  };

  const initialiseSection = async (section) => {
    if (!(section instanceof HTMLElement) || section.dataset.pevGoogleInitialised === 'true') return;

    const liveContainer = section.querySelector('[data-pev-google-live]');
    if (!liveContainer) return;

    section.dataset.pevGoogleInitialised = 'true';

    const apiKey = (section.dataset.apiKey || '').trim();
    const placeId = (section.dataset.placeId || '').trim();
    const placeQuery = (section.dataset.placeQuery || '').trim();
    const reviewLimit = Math.max(1, Math.min(5, Number.parseInt(section.dataset.reviewLimit || '3', 10)));

    if (!apiKey || (!placeId && !placeQuery)) {
      showFailure(section, 'Add a restricted Google Maps browser API key and the exact Place ID in Theme settings.');
      return;
    }

    try {
      await loadGoogleMaps(apiKey);
      const { Place } = await window.google.maps.importLibrary('places');
      const { place: requestedPlace, usedTextSearch } = await getPlace(Place, placeId, placeQuery);
      const response = await requestedPlace.fetchFields({
        fields: [
          'displayName',
          'rating',
          'userRatingCount',
          'reviews',
          'googleMapsLinks',
          'googleMapsURI',
          'attributions',
        ],
      });
      const place = response?.place || requestedPlace;
      const reviews = Array.isArray(place.reviews) ? place.reviews.slice(0, reviewLimit) : [];

      if (reviews.length === 0) {
        throw new Error('Google Maps returned no reviews for this Place ID.');
      }

      const grid = section.querySelector('[data-pev-google-grid]');
      const loading = section.querySelector('[data-pev-google-loading]');
      const error = section.querySelector('[data-pev-google-error]');
      const disclosure = section.querySelector('[data-pev-google-disclosure]');
      const fallback = section.querySelector('[data-pev-google-fallback-content]');

      if (!grid) throw new Error('The live Google review container is missing.');

      grid.replaceChildren(...reviews.map(createReviewCard));
      grid.hidden = false;
      if (loading) loading.hidden = true;
      if (error) error.hidden = true;
      if (fallback) fallback.hidden = true;
      if (disclosure) disclosure.hidden = false;
      liveContainer.setAttribute('aria-busy', 'false');

      updateRating(section, place);
      updateLinks(section, place);
      updateThirdPartyAttributions(section, place);
      setSearchDisclosure(section, usedTextSearch);
    } catch (error) {
      console.warn('[Powered Up PEV] Google Maps reviews were not loaded.', error);
      showFailure(section, error instanceof Error ? error.message : 'Google Maps reviews could not be loaded.');
    }
  };

  const reviewObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          initialiseSection(entry.target);
        });
      }, { rootMargin: '450px 0px' })
    : null;

  const queueSection = (section, immediate = false) => {
    if (!(section instanceof HTMLElement) || section.dataset.pevGoogleInitialised === 'true') return;
    if (immediate || !reviewObserver) {
      initialiseSection(section);
      return;
    }
    if (section.dataset.pevGoogleObserved === 'true') return;
    section.dataset.pevGoogleObserved = 'true';
    reviewObserver.observe(section);
  };

  const queueAll = (root = document, immediate = false) => {
    if (root.matches?.('[data-pev-google-reviews]')) queueSection(root, immediate);
    root.querySelectorAll?.('[data-pev-google-reviews]').forEach((section) => queueSection(section, immediate));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => queueAll(), { once: true });
  } else {
    queueAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    queueAll(event.target, true);
  });
})();

// Powered Up PEV build 7.4.2 unified reviews
